import React, { useState, useMemo, useEffect } from 'react'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Paper from '@material-ui/core/Paper'
import Collapse from '@material-ui/core/Collapse'
import CPVSelector from './CPVSelector'
import AdvancedSearch from './advancedSearch'
import SvgIcon from '@material-ui/core/SvgIcon'
import { ReactComponent as IconSearch } from 'assets/icons/search.svg'
import useTranslation from 'hooks/useTranslations'
import useStyles from './styles'
import { getProcurementInfo } from 'services/procurement/actions'
import { getProcurementVendorTypesInformation } from 'services/procurement/selectors'
import { loadLocale } from 'services/locale'
import { useDispatch, useSelector } from 'react-redux'

const NewSearch = props => {
  const {
    hasButton,
    genericError,
    clearSearchOptions,
    onSearch,
    searchConfig,
    initialValues,
    onConfigChange,
  } = props

  const classes = useStyles()
  const t = useTranslation()
  const dispatch = useDispatch()

  const locale = loadLocale() || 'es'
  const procumerementVendorTypes = useSelector(
    getProcurementVendorTypesInformation,
  )

  const [expanded, setExpanded] = useState(false)
  const [advancedExpanded, setAdvancedExpanded] = useState(false)
  const [CPVLvl1, setCPVLvl1] = useState(null)
  const [CPVLvl2, setCPVLvl2] = useState(null)
  const [CPVLvl3, setCPVLvl3] = useState(null)
  const [vendorTypes, setVendorTypes] = useState([])
  const [country, setCountry] = useState(null)
  const [provinces, setProvinces] = useState([])
  const [searchNetwork, setSearchNetwork] = useState('0')
  const [documentsAndCertificates, setDocumentsAndCertificates] = useState([])
  const [invalidDocuments, setInvalidDocuments] = useState('0')
  const [initialParams, setInitialParams] = useState('')

  useEffect(() => {
    if (0 < searchConfig.vendorTypes.length) {
      dispatch(getProcurementInfo(locale))
    }
  }, [dispatch, locale, searchConfig.vendorTypes])

  useEffect(() => {
    if (
      initialValues &&
      (initialValues.CPV ||
        initialValues.documents ||
        initialValues.vendor_types) &&
      !initialParams
    ) {
      if (initialValues.CPV) {
        if (initialValues.CPV[0]) {
          setCPVLvl1(initialValues.CPV[0])
        }
        if (initialValues.CPV[1]) {
          setCPVLvl2(initialValues.CPV[1])
        }
        if (initialValues.CPV[2]) {
          setCPVLvl3(initialValues.CPV[2])
        }
      }
      if (initialValues.vendor_types) {
        setVendorTypes(initialValues.vendor_types)
      }
      if (initialValues.country) {
        setCountry(initialValues.country)
      }
      if (initialValues.provinces) {
        setProvinces(initialValues.provinces)
      }
      if (initialValues.search_network) {
        setSearchNetwork(initialValues.search_network.toString())
      }
      if (initialValues.documents) {
        setDocumentsAndCertificates(initialValues.documents)
      }
      if (initialValues.invalid_documents) {
        setInvalidDocuments(initialValues.invalid_documents.toString())
      }
    }
  }, [initialParams, initialValues])

  const CPVValues = useMemo(
    () => ({ lvl1: CPVLvl1, lvl2: CPVLvl2, lvl3: CPVLvl3 }),
    [CPVLvl1, CPVLvl2, CPVLvl3],
  )

  const CPVOptions = useMemo(() => {
    const { CPVs } = searchConfig
    const lvl1 = CPVs
    const lvl2 = CPVLvl1 ? CPVLvl1.sublevel || [] : []
    const lvl3 = CPVLvl2 ? CPVLvl2.sublevel || [] : []

    return { lvl1, lvl2, lvl3 }
  }, [searchConfig, CPVLvl1, CPVLvl2])

  const handleExpandClick = () => {
    setExpanded(!expanded)
  }

  const queryParams = useMemo(() => {
    const params = []

    if (CPVLvl1) {
      params.push(
        `cpv=${CPVLvl3 ? CPVLvl3.id : CPVLvl2 ? CPVLvl2.id : CPVLvl1.id}`,
      )
    }
    if (vendorTypes && vendorTypes.length) {
      params.push(
        `vendor_types[]=${vendorTypes.map(type => type.id).join(',')}`,
      )
    }
    if (country) {
      params.push(`country=${country.id}`)
    }
    if (provinces.length) {
      params.push(
        `states[]=${provinces.map(province => province.id).join(',')}`,
      )
    }
    if (searchNetwork && parseInt(searchNetwork) !== 0) {
      params.push(`search_network=${searchNetwork}`)
    }
    if (documentsAndCertificates.length) {
      params.push(
        `documents[]=${documentsAndCertificates
          .map(document => document.id)
          .join(',')}`,
      )
    }
    if (invalidDocuments && parseInt(invalidDocuments) !== 0) {
      params.push(`invalid_documents=${invalidDocuments}`)
    }

    const stringParams = params.join('&')

    if (!initialParams) {
      setInitialParams(stringParams)
    }

    return stringParams
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    CPVLvl1,
    CPVLvl2,
    CPVLvl3,
    vendorTypes,
    country,
    searchNetwork,
    documentsAndCertificates,
    provinces,
    invalidDocuments,
  ])

  const handleSearch = () => {
    onSearch(queryParams)
    setExpanded(false)
  }

  useEffect(() => {
    if (!initialParams || initialParams === queryParams) return
    const data = {
      CPV: [CPVLvl1, CPVLvl2, CPVLvl3].filter(cpv => cpv),
      vendor_types: vendorTypes,
      country,
      provinces,
      search_network: searchNetwork,
      documents: documentsAndCertificates,
      invalid_documents: invalidDocuments,
    }
    onConfigChange(data)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    CPVLvl1,
    CPVLvl2,
    CPVLvl3,
    vendorTypes,
    country,
    provinces,
    searchNetwork,
    documentsAndCertificates,
    invalidDocuments,
  ])

  const Content = () => (
    <Box p={4}>
      <CPVSelector
        options={CPVOptions}
        getSearchOptions={() => {}}
        clearSearchOptions={clearSearchOptions}
        genericError={genericError}
        onSearch={handleSearch}
        values={CPVValues}
        setValues={{ lvl1: setCPVLvl1, lvl2: setCPVLvl2, lvl3: setCPVLvl3 }}
      />
      {(0 === searchConfig.vendorTypes.length ||
        0 < procumerementVendorTypes.length) && (
        <AdvancedSearch
          vendorTypesOptions={procumerementVendorTypes}
          vendorTypes={vendorTypes}
          setVendorTypes={setVendorTypes}
          countries={searchConfig.countries}
          country={country}
          setCountry={setCountry}
          provincesOptions={searchConfig.provinces}
          provinces={provinces}
          setProvinces={setProvinces}
          searchNetwork={searchNetwork}
          setSearchNetwork={setSearchNetwork}
          documentsAndCertificatesOptions={searchConfig.documents.concat(
            searchConfig.requirements,
          )}
          documentsAndCertificates={documentsAndCertificates}
          setDocumentsAndCertificates={setDocumentsAndCertificates}
          invalidDocuments={invalidDocuments}
          setInvalidDocuments={setInvalidDocuments}
          expanded={advancedExpanded}
          setExpanded={setAdvancedExpanded}
          onSearch={handleSearch}
        />
      )}
      <Box
        display="flex"
        justifyContent="flex-end"
        className={classes.applySearchButtonWrapper}
      >
        <Button
          variant="contained"
          color="primary"
          disabled={
            !CPVLvl1 &&
            documentsAndCertificates.length === 0 &&
            vendorTypes.length === 0
          }
          onClick={handleSearch}
        >
          {t('search:apply-search')}
        </Button>
      </Box>
    </Box>
  )

  return (
    <Box mb={5} className={classes.demo}>
      <Paper className={clsx(hasButton && classes.paper)}>
        {hasButton ? (
          <>
            <Collapse
              className={clsx(!expanded && classes.isCollapsed)}
              in={expanded}
              collapsedHeight={178}
            >
              <Content />
            </Collapse>
            <div className={classes.buttonWrapper}>
              <Button
                className={classes.button}
                onClick={handleExpandClick}
                variant="contained"
                color="primary"
                size="small"
                startIcon={<SvgIcon component={IconSearch} />}
              >
                {t('search:change-search')}
              </Button>
            </div>
          </>
        ) : (
          <Content />
        )}
      </Paper>
    </Box>
  )
}

NewSearch.propTypes = {
  hasButton: PropTypes.bool,
  searchConfig: PropTypes.object,
}

export default NewSearch
